import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { ConfigService } from '../../config/services/config.service';
import {
  LicenseGroupOverrideList,
  LicenseNihiiOverrideList,
  LicenseOverrideType,
  LicenseManagerOverrideResponse,
  LicenseOptionsResponse,
} from '../models/license-override-list.model';
import { LicenseActiveGroupResponse } from '../models/license-active-group.model';
import { NihiiHelper } from '../../helpers/nihii.helper';

export interface FirebaseAuthLike {
  readonly currentUser: FirebaseUserLike | null;
}

interface FirebaseUserLike {
  readonly uid: string;
}

export const LICENSE_FIREBASE_AUTH = new InjectionToken<FirebaseAuthLike>(
  'LICENSE_FIREBASE_AUTH',
);

@Injectable({
  providedIn: 'root',
})
export class LicenseService {
  private groupOverrides: LicenseGroupOverrideList | null = null;
  private nihiiOverrides: LicenseNihiiOverrideList | null = null;

  constructor(
    private configService: ConfigService,
    @Optional()
    @Inject(LICENSE_FIREBASE_AUTH)
    private firebaseAuth?: FirebaseAuthLike,
  ) {}

  public async loadGroupOverrideList(token: string): Promise<void> {
    token = await this.getCurrentToken(token);
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    };

    this.groupOverrides =
      await this.fetchOverrideList<LicenseGroupOverrideList>(
        '/license/v1/override/groups',
        headers,
      );
  }

  public async loadNihiiOverrideList(token: string): Promise<void> {
    token = await this.getCurrentToken(token);
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    };

    this.nihiiOverrides =
      await this.fetchOverrideList<LicenseNihiiOverrideList>(
        '/license/v1/override/nihii',
        headers,
      );
  }

  public async loadManagerOverrideList(
    token: string,
  ): Promise<LicenseManagerOverrideResponse> {
    token = await this.getCurrentToken(token);
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    };

    return this.fetchOverrideList<LicenseManagerOverrideResponse>(
      '/license/v1/override',
      headers,
    );
  }

  public async loadOptionsList(token: string): Promise<LicenseOptionsResponse> {
    token = await this.getCurrentToken(token);
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    };

    return this.fetchOverrideList<LicenseOptionsResponse>(
      '/license/v1/options',
      headers,
    );
  }

  public async loadActiveGroups(
    token?: string,
  ): Promise<LicenseActiveGroupResponse> {
    token = await this.getCurrentToken(token);
    const headers: Record<string, string> = {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    };

    return this.fetchOverrideList<LicenseActiveGroupResponse>(
      '/license/v1/active-groups',
      headers,
    );
  }

  public async deleteOption(
    token: string,
    nihii: string,
    option: string,
  ): Promise<void> {
    token = await this.getCurrentToken(token);
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    };
    const query = new URLSearchParams({ nihii, option });

    await this.deleteOverride(
      `/license/v1/option?${query.toString()}`,
      headers,
    );
  }

  public async createOption(
    token: string,
    nihii: string,
    option: string,
  ): Promise<void> {
    token = await this.getCurrentToken(token);
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    };
    const query = new URLSearchParams({ nihii, option });

    await this.putOverride(`/license/v1/option?${query.toString()}`, headers);
  }

  public async deleteGroupOverride(
    token: string,
    groupId: string,
  ): Promise<void> {
    token = await this.getCurrentToken(token);
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    };

    await this.deleteOverride(
      `/license/v1/override/group/${encodeURIComponent(groupId)}`,
      headers,
    );
  }

  public async createGroupOverride(
    token: string,
    groupId: string,
    overrideType: LicenseOverrideType,
    reason: string,
  ): Promise<void> {
    token = await this.getCurrentToken(token);
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    };
    const query = new URLSearchParams({
      groupId,
      overrideType,
      reason,
    });

    await this.putOverride(
      `/license/v1/override/group?${query.toString()}`,
      headers,
    );
  }

  public async deleteNihiiOverride(
    token: string,
    nihii: string,
  ): Promise<void> {
    token = await this.getCurrentToken(token);
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    };

    await this.deleteOverride(
      `/license/v1/override/nihii/${encodeURIComponent(nihii)}`,
      headers,
    );
  }

  public async createNihiiOverride(
    token: string,
    nihii: string,
    overrideType: LicenseOverrideType,
    reason: string,
  ): Promise<void> {
    token = await this.getCurrentToken(token);
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    };
    const query = new URLSearchParams({
      nihii,
      overrideType,
      reason,
    });

    await this.putOverride(
      `/license/v1/override/nihii?${query.toString()}`,
      headers,
    );
  }

  public getGroupOverrideType(groupId: string): LicenseOverrideType | null {
    return (
      this.groupOverrides?.groups.find((group) => group.group_id === groupId)
        ?.override_type ?? null
    );
  }

  public hasGroupOverride(groupId: string): boolean {
    return this.getGroupOverrideType(groupId) !== null;
  }

  public getNihiiOverrideType(nihii: string): LicenseOverrideType | null {
    const normalizedNihii = NihiiHelper.normalizeToNihii8(nihii);
    return (
      this.nihiiOverrides?.nihiis.find((item) =>
        normalizedNihii.startsWith(item.nihii),
      )?.override_type ?? null
    );
  }

  public hasNihiiOverride(nihii: string): boolean {
    return this.getNihiiOverrideType(nihii) !== null;
  }

  public getGroupOverrides(): LicenseGroupOverrideList | null {
    return this.groupOverrides;
  }

  public getNihiiOverrides(): LicenseNihiiOverrideList | null {
    return this.nihiiOverrides;
  }

  private async getCurrentToken(token: string): Promise<string>;
  private async getCurrentToken(token?: string): Promise<string | undefined>;
  private async getCurrentToken(token?: string): Promise<string | undefined> {
    const currentUser = this.firebaseAuth?.currentUser;
    if (!currentUser) {
      return token;
    }

    const { getIdToken } = await import('firebase/auth');
    return getIdToken(currentUser as Parameters<typeof getIdToken>[0]);
  }

  private async fetchOverrideList<T>(
    path: string,
    headers: Record<string, string>,
  ): Promise<T> {
    const response = await fetch(
      `${this.configService.licenseServiceUrl}${path}`,
      {
        method: 'GET',
        mode: 'cors',
        headers,
      },
    );

    const contentType = response.headers.get('content-type') ?? '';
    const body = contentType.includes('application/json')
      ? ((await response.json()) as T)
      : await response.text();

    if (!response.ok) {
      throw new Error(
        `License Service request failed for ${path} with status ${response.status}`,
      );
    }

    return body as T;
  }

  private async deleteOverride(
    path: string,
    headers: Record<string, string>,
  ): Promise<void> {
    const response = await fetch(
      `${this.configService.licenseServiceUrl}${path}`,
      {
        method: 'DELETE',
        mode: 'cors',
        headers,
      },
    );

    if (!response.ok) {
      throw new Error(
        `License Service request failed for ${path} with status ${response.status}`,
      );
    }
  }

  private async putOverride(
    path: string,
    headers: Record<string, string>,
  ): Promise<void> {
    const response = await fetch(
      `${this.configService.licenseServiceUrl}${path}`,
      {
        method: 'PUT',
        mode: 'cors',
        headers,
      },
    );

    if (!response.ok) {
      throw new Error(
        `License Service request failed for ${path} with status ${response.status}`,
      );
    }
  }
}
